import React, { useEffect, useState } from "react";
import { userService } from "../services/userService";
import { useLocation, useNavigate } from "react-router-dom";
import { LuEllipsisVertical, LuPencil, LuX } from "react-icons/lu";
import RemoveUserIcon from "../icons/RemoveUserIcon";
import DeleteRoleIcon from "../icons/DeleteRoleIcon";
import roleController from "../controllers/roleController";
import { translateLabel, useTranslationSnapshot } from "./../translations/runtime";


const APPLICATION_ID = 3;

const roleFeedbackLabels = new Map([
  ["Company id was not found for this user.", "txt_web_vendor_company_id_was_not_found_for_this_user"],
  ["Unable to load roles.", "txt_web_vendor_unable_to_load_roles"],
  ["Unable to delete role.", "txt_web_vendor_unable_to_delete_role"],
]);
const translateRoleFeedback = (message) => {
  const id = roleFeedbackLabels.get(message);
  return id ? translateLabel(id, message) : message;
};
const displayRoleName = (role) => role.hasSourceName
  ? role.name : translateLabel("txt_web_vendor_unnamed_role", "Unnamed Role");

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? [];

const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const mapRole = (role) => ({
  defaultRole: Boolean(role.defaultRole),
  description: role.roleDescription || role.description || "",
  id: role.roleID || role.roleId || role.id,
  name: role.roleName || role.name || "Unnamed Role",
  hasSourceName: Boolean(role.roleName || role.name),
  permissions: Array.isArray(role.permissions) ? role.permissions : [],
});

function UserManagementRolesPermissions() {
  useTranslationSnapshot();
  const location = useLocation();
  const navigate = useNavigate();
  const isPreview = location.pathname.includes("-preview");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchRoles = async () => {
      setLoading(true);
      setError("");

      try {
        const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

        if (!companyId) {
          throw new Error("Company id was not found for this user.");
        }

        const response = await roleController.getAllRoles({
          Authorization,
          application: APPLICATION_ID,
          companyId,
        });

        if (response?.isAxiosError || response?.response) {
          throw response;
        }

        if (!isCurrentRequest) return;

        setRoles(
          getRows(unwrapPayload(response))
            .map(mapRole)
            .filter((role) => role.id),
        );
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load roles.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchRoles();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  const openEditRole = (roleId) => {
    const path = isPreview
      ? `/vendor-edit-role-preview/${roleId}`
      : `/vendor-user-management/roles/edit/${roleId}`;
    navigate(path);
  };

  const removeRole = async () => {
    if (!roleToDelete) return;

    setDeleting(true);
    setError("");

    try {
      const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

      if (!companyId) {
        throw new Error("Company id was not found for this user.");
      }

      const response = await roleController.deleteRole({
        Authorization,
        deleteRoleRequest: {
          companyId,
          roleId: roleToDelete.id,
        },
      });

      if (response?.isAxiosError || response?.response) {
        throw response;
      }

      setRoles((currentRoles) =>
        currentRoles.filter((role) => role.id !== roleToDelete.id),
      );
      setRoleToDelete(null);
      setOpenMenuId(null);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to delete role.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="vendor-roles-panel">
      <div className="vendor-roles-table-wrap">
        <table className="vendor-roles-table">
          <thead>
            <tr>
              <th>{translateLabel("txt_web_vendor_roles", "Roles")}</th>
              <th>{translateLabel("input_business_description", "Description")}</th>
              <th>{translateLabel("txt_common_actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              !error &&
              roles.map((role) => (
                <tr key={role.id}>
                  <td>{displayRoleName(role)}</td>
                  <td>{role.description}</td>
                  <td>
                    <div className="vendor-user-actions">
                      <button
                        className="vendor-user-actions-button"
                        type="button"
                        aria-label={translateLabel("txt_web_vendor_actions_for_value1", "Actions for {value1}", { value1: displayRoleName(role) })}
                        aria-expanded={openMenuId === role.id}
                        onClick={() =>
                          setOpenMenuId((current) =>
                            current === role.id ? null : role.id,
                          )
                        }
                      >
                        <LuEllipsisVertical aria-hidden="true" />
                      </button>

                      {openMenuId === role.id && (
                        <div className="vendor-user-action-menu vendor-role-action-menu">
                          <button
                            type="button"
                            onClick={() => openEditRole(role.id)}
                          >
                            <LuPencil aria-hidden="true" />
                            <span>{translateLabel("txt_web_vendor_edit_details", "Edit Details")}</span>
                          </button>
                          {!role.defaultRole && (
                            <button
                              type="button"
                              onClick={() => {
                                setRoleToDelete(role);
                                setOpenMenuId(null);
                              }}
                            >
                              <RemoveUserIcon />
                              <span>{translateLabel("txt_web_vendor_remove_role", "Remove Role")}</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {loading && (
          <div className="vendor-menu-empty-state">{translateLabel("txt_web_vendor_loading_roles", "Loading roles...")}</div>
        )}
        {!loading && error && (
          <div className="vendor-menu-empty-state" role="alert">{translateRoleFeedback(error)}</div>
        )}
        {!loading && !error && roles.length === 0 && (
          <div className="vendor-menu-empty-state">{translateLabel("txt_web_vendor_no_roles_found", "No roles found.")}</div>
        )}
      </div>

      {roleToDelete && (
        <div className="vendor-delete-user-backdrop">
          <div
            className="vendor-delete-user-modal vendor-delete-role-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vendor-delete-role-title"
          >
            <button
              className="vendor-delete-user-close"
              type="button"
              aria-label={translateLabel("txt_web_vendor_close_delete_role", "Close Delete Role")}
              disabled={deleting}
              onClick={() => setRoleToDelete(null)}
            >
              <LuX aria-hidden="true" />
            </button>

            <div className="vendor-delete-role-icon">
              <DeleteRoleIcon />
            </div>
            <div className="vendor-delete-user-title" id="vendor-delete-role-title">
              {translateLabel("txt_web_vendor_delete_role_title", "Delete Role")}
            </div>
            <div className="vendor-delete-user-message">
              {translateLabel("txt_web_vendor_delete_role_confirmation", "Are you sure you want to delete this role?")}
            </div>
            <div className="vendor-delete-user-actions">
              <button type="button" disabled={deleting} onClick={removeRole}>
                {deleting ? translateLabel("txt_end_user_deleting", "Deleting...") : translateLabel("lnk_delete", "Delete")}
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => setRoleToDelete(null)}
              > {translateLabel("btn_cancel", "Cancel")} </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default UserManagementRolesPermissions;
